import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { markDancerClaimsAsSeen } from './hooks/useUnseenClaims';
import './AdminClaimReview.css';

// Helper function to calculate age category from birth date
const calculateAgeCategory = (birthDate) => {
  if (!birthDate) {
    return null;
  }
  
  try {
    let day, month, year;
    
    // Handle both dd/mm/yyyy and yyyy-mm-dd formats
    if (birthDate.includes('-')) {
      // yyyy-mm-dd format (from database)
      const parts = birthDate.split('-');
      if (parts.length !== 3) {
        return null;
      }
      [year, month, day] = parts.map(num => parseInt(num));
    } else if (birthDate.includes('/')) {
      // dd/mm/yyyy format (from form input)
      const parts = birthDate.split('/');
      if (parts.length !== 3) {
        return null;
      }
      [day, month, year] = parts.map(num => parseInt(num));
    } else {
      return null;
    }
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return null;
    }
    
    const birthYear = year;
    const currentYear = new Date().getFullYear();
    
    // Calculate age based on year only (as of January 1st of current year)
    const age = currentYear - birthYear;
    
    if (age >= 7 && age <= 9) return 'Juvenile I';
    if (age >= 10 && age <= 11) return 'Juvenile II';
    if (age >= 12 && age <= 13) return 'Junior I';
    if (age >= 14 && age <= 15) return 'Junior II';
    if (age >= 16 && age <= 18) return 'Youth';
    if (age >= 19) return 'Adult';
    
    // For ages below 7, return null (no category)
    return null;
  } catch (error) {
    return null;
  }
};

const AdminClaimReview = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending'); // Changed default to 'pending' for faster loading
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [expandedDocuments, setExpandedDocuments] = useState(new Set());
  const [dancerDocuments, setDancerDocuments] = useState(new Map()); // Store documents for each dancer
  const [dancerProfilePics, setDancerProfilePics] = useState(new Map()); // Store profile pictures for each dancer
  
  // Rejection reasons state
  const [rejectionReasons, setRejectionReasons] = useState(new Map()); // claimId -> {reasons: [], showDialog: boolean}
  
  // Lazy loading: track which claims are expanded and loading
  const [expandedClaims, setExpandedClaims] = useState(new Set()); // Which claims are expanded to show full details
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // Which claims are currently loading details

  // Admin DOB overrides: claimId -> edited date string (yyyy-mm-dd)
  const [adminDobOverrides, setAdminDobOverrides] = useState({});

  // Admin name overrides: claimId -> new name string
  const [adminNameOverrides, setAdminNameOverrides] = useState({});

  // Studios per dancer: dancerName -> string[]
  const [dancerStudios, setDancerStudios] = useState(new Map());

  // Dancers whose picture/studio lookup has already started
  const extrasRequested = useRef(new Set());

  // Claims are fetched once (all statuses); the tabs filter in memory so counts stay correct
  // and switching tabs is instant.
  useEffect(() => {
    fetchClaims();
  }, []);

  const visibleClaims = statusFilter === 'all'
    ? claims
    : claims.filter(c => c.status === statusFilter);

  // Load thumbnails/studios for whichever tab is showing (skips dancers already loaded)
  useEffect(() => {
    if (visibleClaims.length > 0) loadProfilePicsForClaims(visibleClaims);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, statusFilter]);

  const fetchDancerDocuments = async (dancerName) => {
    try {
      const documents = [];
      const sanitizedDancerName = dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      
      // First, try the new organized structure: verification-docs/DancerName/
      try {
        const dancerFolderRef = ref(storage, `verification-docs/${sanitizedDancerName}`);
        const folderResult = await listAll(dancerFolderRef);
        
        console.log(`🔍 Checking organized folder: verification-docs/${sanitizedDancerName}`);
        console.log(`📁 Found ${folderResult.items.length} files in dancer's folder`);
        
        for (const itemRef of folderResult.items) {
          try {
            const downloadURL = await getDownloadURL(itemRef);
            documents.push({
              name: itemRef.name,
              fullPath: itemRef.fullPath,
              url: downloadURL,
              source: 'organized-folder'
            });
            console.log(`✅ Added document from organized folder: ${itemRef.name}`);
          } catch (error) {
            console.log(`Could not get download URL for ${itemRef.fullPath}:`, error);
          }
        }
      } catch (folderError) {
        console.log(`📁 No organized folder found for ${sanitizedDancerName}:`, folderError.message);
      }
      
      // If nothing found in organized folder, search all verification-docs files by name matching
      if (documents.length === 0) {
        console.log(`🔍 Searching all verification-docs files for name matches...`);
        try {
          const verificationFolderRef = ref(storage, `verification-docs`);
          const verificationResult = await listAll(verificationFolderRef);
          
          const dancerNameParts = dancerName.toLowerCase().split(' ');
          
          for (const itemRef of verificationResult.items) {
            const fileName = itemRef.name.toLowerCase();
            
            // Check if any part of the dancer's name is in the filename
            const nameMatch = dancerNameParts.some(part => {
              const cleanPart = part.replace(/[^a-z]/g, '');
              return fileName.includes(part) || 
                     fileName.includes(cleanPart) ||
                     fileName.replace(/[^a-z]/g, '').includes(cleanPart);
            });
            
            if (nameMatch) {
              try {
                const downloadURL = await getDownloadURL(itemRef);
                documents.push({
                  name: itemRef.name,
                  fullPath: itemRef.fullPath,
                  url: downloadURL,
                  source: 'name-match'
                });
                console.log(`✅ Added document from name match: ${itemRef.name}`);
              } catch (error) {
                console.log(`Could not get download URL for ${itemRef.fullPath}:`, error);
              }
            }
          }
        } catch (error) {
          console.log(`Could not search verification-docs folder:`, error);
        }
      }
      
      // Finally, try the old dancers folder structure as last resort
      if (documents.length === 0) {
        try {
          const dancerFolderRef = ref(storage, `dancers/${dancerName}`);
          const dancerResult = await listAll(dancerFolderRef);
          
          for (const itemRef of dancerResult.items) {
            try {
              const downloadURL = await getDownloadURL(itemRef);
              documents.push({
                name: itemRef.name,
                fullPath: itemRef.fullPath,
                url: downloadURL,
                source: 'legacy-folder'
              });
              console.log(`✅ Added document from legacy folder: ${itemRef.name}`);
            } catch (error) {
              console.log(`Could not get download URL for ${itemRef.fullPath}:`, error);
            }
          }
        } catch (error) {
          console.log(`No documents found in legacy dancers folder for: ${dancerName}`, error);
        }
      }
      
      return documents;
    } catch (error) {
      console.log(`No documents found for dancer: ${dancerName}`, error);
      return [];
    }
  };

  // Helper to convert storage path to full URL
  const getStorageURL = async (pathOrURL) => {
    if (pathOrURL.startsWith('http://') || pathOrURL.startsWith('https://')) {
      return pathOrURL;
    }
    try {
      return await getDownloadURL(ref(storage, pathOrURL));
    } catch (error) {
      return null;
    }
  };

  // Load profile picture + studios for one dancer: each doc is read once, in parallel.
  // The thumbnail <img> has its own onError fallback, so we don't pre-download the image to test it.
  const fetchDancerExtras = async (dancerName) => {
    const studios = new Set();
    let picSource = null;
    try {
      const [profileSnap, dancerSnap] = await Promise.all([
        getDoc(doc(db, 'dancerProfiles', dancerName)),
        getDoc(doc(db, 'dancers', dancerName)),
      ]);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        picSource = data.profilePictureURL || data.profilePicture || data.photoURL || null;
        if (data.disciplineStudios && typeof data.disciplineStudios === 'object') {
          Object.values(data.disciplineStudios).forEach(s => { if (s) studios.add(s); });
        }
        if (data.studio) studios.add(data.studio);
      }
      if (dancerSnap.exists()) {
        const data = dancerSnap.data();
        if (!picSource) {
          picSource = data.profilePictureURL || data.profilePicture || data.photoURL || null;
        }
        if (data.studio) studios.add(data.studio);
      }
    } catch (e) {}

    const url = picSource ? await getStorageURL(picSource) : null;
    return { url, studios: [...studios] };
  };

  // Runs in the background after the claims list is already on screen.
  // Results are merged into state as each dancer resolves, so thumbnails pop in progressively.
  const loadProfilePicsForClaims = (claimsData) => {
    const dancerNames = [...new Set(claimsData.map(c => c.dancerName).filter(Boolean))]
      .filter(name => !extrasRequested.current.has(name));

    dancerNames.forEach(async (dancerName) => {
      extrasRequested.current.add(dancerName);
      const { url, studios } = await fetchDancerExtras(dancerName);
      if (url) {
        setDancerProfilePics(prev => new Map(prev).set(dancerName, url));
      }
      if (studios.length > 0) {
        setDancerStudios(prev => new Map(prev).set(dancerName, studios));
      }
    });
  };

  // Load documents for a single claim (on-demand when expanding)
  const loadDocumentsForSingleClaim = async (claim) => {
    // Skip if already loaded
    if (dancerDocuments.has(claim.dancerName)) {
      return;
    }
    
    console.log(`📋 Loading documents for: ${claim.dancerName}`);
    
    let documents = [];
    
    // First, check if the claim has the new document path stored
    if (claim.documentPath) {
      console.log(`📁 Found stored document path: ${claim.documentPath}`);
      try {
        const docRef = ref(storage, claim.documentPath);
        const downloadURL = await getDownloadURL(docRef);
        documents = [{
          name: claim.documentFileName || claim.documentPath.split('/').pop(),
          fullPath: claim.documentPath,
          url: downloadURL,
          source: 'stored-path'
        }];
        console.log(`✅ Document loaded from stored path for ${claim.dancerName}`);
      } catch (error) {
        console.log(`❌ Could not load document from stored path: ${error.message}`);
      }
    }
    
    // If no stored path or it failed, use the fetchDancerDocuments function
    if (documents.length === 0) {
      documents = await fetchDancerDocuments(claim.dancerName);
    }
    
    // Update the documents map
    setDancerDocuments(prev => {
      const newMap = new Map(prev);
      if (documents.length > 0) {
        newMap.set(claim.dancerName, documents);
      }
      return newMap;
    });
    
    console.log(`📄 Found ${documents.length} documents for ${claim.dancerName}`);
  };

  // Toggle claim expansion (lazy load documents when expanding)
  const toggleClaimExpansion = async (claimId, claim) => {
    const newExpanded = new Set(expandedClaims);
    
    if (newExpanded.has(claimId)) {
      // Collapse
      newExpanded.delete(claimId);
    } else {
      // Expand and load documents if needed
      newExpanded.add(claimId);
      
      // Start loading documents if not already loaded
      if (!dancerDocuments.has(claim.dancerName)) {
        setLoadingDetails(prev => new Set([...prev, claimId]));
        try {
          await loadDocumentsForSingleClaim(claim);
        } finally {
          setLoadingDetails(prev => {
            const newSet = new Set(prev);
            newSet.delete(claimId);
            return newSet;
          });
        }
      }
    }
    
    setExpandedClaims(newExpanded);
  };

  const fetchClaims = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'profileClaims'));
      
      const claimsData = [];
      querySnapshot.forEach((doc) => {
        claimsData.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by submission date (newest first)
      claimsData.sort((a, b) => {
        const dateA = a.submittedAt?.toDate() || new Date(0);
        const dateB = b.submittedAt?.toDate() || new Date(0);
        return dateB - dateA;
      });
      
      setClaims(claimsData);
      
      // Mark all pending claims as seen when admin views the page
      const pendingClaimIds = claimsData
        .filter(claim => claim.status === 'pending')
        .map(claim => claim.id);
      
      if (pendingClaimIds.length > 0) {
        markDancerClaimsAsSeen(pendingClaimIds);
      }
      
      // Thumbnails/studios load in the background (see effect on visibleClaims);
      // documents are loaded on-demand when expanding a claim.

    } catch (err) {
      setError('Failed to load claims: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClaim = async (claimId, dancerName, userId, overrideDob) => {
    try {
      console.log('Approving claim:', { claimId, dancerName, userId });

      const claim = claims.find(c => c.id === claimId);
      if (!claim) {
        alert('Claim not found');
        return;
      }

      // Use admin-overridden DOB if provided, otherwise fall back to claim's DOB
      const effectiveBirthDate = overrideDob || adminDobOverrides[claimId] || claim.birthDate;

      // Determine the final name (admin can override the name before approving)
      const adminNameOverride = adminNameOverrides[claimId]?.trim();
      const effectiveName = (adminNameOverride && adminNameOverride !== dancerName)
        ? adminNameOverride
        : dancerName;
      const nameChanged = effectiveName !== dancerName;

      // If a name change was requested, rename all competition results first
      if (nameChanged) {
        const batch = writeBatch(db);

        function applyRename(docRef, comp) {
          let hasUpdates = false;
          if (comp.results) {
            comp.results.forEach((result) => {
              if (result.dancerName === dancerName) { result.dancerName = effectiveName; hasUpdates = true; }
            });
          }
          if (comp.roundData) {
            comp.roundData.forEach((rd) => {
              if (rd.dancerName === dancerName) { rd.dancerName = effectiveName; hasUpdates = true; }
            });
          }
          if (hasUpdates) {
            const payload = { results: comp.results };
            if (comp.roundData) payload.roundData = comp.roundData;
            batch.update(docRef, payload);
          }
        }

        const oldSnapshot = await getDocs(collection(db, 'competitions'));
        oldSnapshot.forEach(compDoc => applyRename(compDoc.ref, compDoc.data()));

        const disciplinesSnapshot = await getDocs(collection(db, 'disciplines'));
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          const categoriesSnapshot = await getDocs(collection(db, 'disciplines', disciplineName, 'categories'));
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            try {
              const directComps = await getDocs(collection(db, 'disciplines', disciplineName, 'categories', categoryName, 'competitions'));
              directComps.forEach(compDoc => applyRename(compDoc.ref, compDoc.data()));
            } catch {}
            try {
              const dances = await getDocs(collection(db, 'disciplines', disciplineName, 'categories', categoryName, 'dances'));
              for (const danceDoc of dances.docs) {
                const danceComps = await getDocs(collection(db, 'disciplines', disciplineName, 'categories', categoryName, 'dances', danceDoc.id, 'competitions'));
                danceComps.forEach(compDoc => applyRename(compDoc.ref, compDoc.data()));
              }
            } catch {}
          }
        }
        await batch.commit();

        // Move profile documents from old name to new name
        for (const colName of ['dancers', 'dancerProfiles']) {
          const oldRef = doc(db, colName, dancerName);
          const oldSnap = await getDoc(oldRef);
          if (oldSnap.exists()) {
            await setDoc(doc(db, colName, effectiveName), oldSnap.data());
            await deleteDoc(oldRef);
          }
        }

        // Move profileVisits document to new name (preserves visitor count)
        const oldVisitsRef = doc(db, 'profileVisits', `dancer_${dancerName}`);
        const oldVisitsSnap = await getDoc(oldVisitsRef);
        if (oldVisitsSnap.exists()) {
          await setDoc(doc(db, 'profileVisits', `dancer_${effectiveName}`), {
            ...oldVisitsSnap.data(),
            profileName: effectiveName
          });
          await deleteDoc(oldVisitsRef);
        }

        // Invalidate stale cache
        try { await deleteDoc(doc(db, 'dancerRankingsCache', dancerName)); } catch {}
      }

      // Update the claim status
      const claimRef = doc(db, 'profileClaims', claimId);
      await updateDoc(claimRef, {
        status: 'approved',
        reviewedAt: new Date(),
        reviewedBy: 'admin',
        ...(nameChanged && { approvedName: effectiveName })
      });

      // Check if dancer document exists, if not create it
      const dancerRef = doc(db, 'dancers', effectiveName);
      const dancerDoc = await getDoc(dancerRef);

      const dancerData = {
        claimedBy: {
          userId: userId,
          email: claim.userEmail || 'unknown',
          approvedAt: new Date()
        },
        claimedAt: new Date()
      };

      if (!dancerDoc.exists()) {
        console.log('Dancer document does not exist, creating it:', effectiveName);
        await setDoc(dancerRef, {
          name: effectiveName,
          ...dancerData,
          profileClaimed: true,
          createdAt: new Date(),
          source: 'profile_claim'
        });
      } else {
        console.log('Updating existing dancer document:', effectiveName);
        await updateDoc(dancerRef, dancerData);
      }

      // Update dancerProfiles collection for consistency
      try {
        const profileRef = doc(db, 'dancerProfiles', effectiveName);
        const profileData = {
          profileClaimed: true,
          claimedBy: {
            userId: userId,
            email: claim.userEmail || 'unknown',
            approvedAt: new Date()
          },
          claimedAt: new Date(),
          name: effectiveName
        };

        // Add Instagram data if available in the claim
        if (claim.instagramUsername) {
          profileData.instagramUsername = claim.instagramUsername.replace('@', '');
        }

        // Add bio data if available in the claim
        if (claim.bio) {
          profileData.bio = claim.bio;
        }

        // Add social media data if available
        if (claim.instagramUsername) {
          profileData.socialMedia = {
            instagram: claim.instagramUsername.replace('@', ''),
            facebook: '',
            tiktok: ''
          };
        }

        // 🎂 Add birth date for age category validation (publicly readable)
        if (effectiveBirthDate) {
          profileData.dateOfBirth = effectiveBirthDate;
          console.log('✅ Copying birth date to public profile:', effectiveBirthDate);
        }

        const profileDoc = await getDoc(profileRef);
        if (!profileDoc.exists()) {
          console.log('Creating new dancerProfiles document:', effectiveName);
          await setDoc(profileRef, profileData);
        } else {
          console.log('Updating existing dancerProfiles document:', effectiveName);
          await updateDoc(profileRef, profileData);
        }

      } catch (profileError) {
        console.error('Error with dancerProfiles document:', profileError);
        alert('Claim approved but there was an issue updating the profile. Please check manually.');
      }

      // Update social media in dancers collection if Instagram is provided
      if (claim.instagramUsername) {
        try {
          await updateDoc(dancerRef, {
            socialMedia: {
              instagram: claim.instagramUsername.replace('@', ''),
              facebook: '',
              tiktok: ''
            },
            bio: claim.bio || '',
            lastUpdated: new Date()
          });
        } catch (socialError) {
          console.log('Note: Could not update social media:', socialError.message);
        }
      }

      // Refresh the claims list
      fetchClaims();
      
      alert('Claim approved successfully!');
    } catch (err) {
      console.error('Error approving claim:', err);
      alert('Failed to change claim status: ' + err.message);
    }
  };

  const handleRejectClaim = async (claimId) => {
    // Show rejection dialog
    console.log('handleRejectClaim called for:', claimId);
    const reasons = rejectionReasons.get(claimId) || { reasons: [], showDialog: false };
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...reasons, showDialog: true });
    console.log('Setting showDialog to true for claim:', claimId, newMap.get(claimId));
    setRejectionReasons(newMap);
  };

  const confirmRejectClaim = async (claimId, isStudioClaim = false) => {
    try {
      const reasons = rejectionReasons.get(claimId);
      const customMessage = (reasons?.customMessage || '').trim();
      if (!reasons || (reasons.reasons.length === 0 && !customMessage)) {
        alert('Please select at least one rejection reason or write a custom message.');
        return;
      }

      const collectionName = isStudioClaim ? 'studioProfileClaims' : 'profileClaims';
      const claimRef = doc(db, collectionName, claimId);

      // The onDancerClaimStatusUpdated / onStudioClaimStatusUpdated Cloud Functions e-mail
      // these fields to the claimant as soon as the status changes to 'rejected'
      await updateDoc(claimRef, {
        status: 'rejected',
        reviewedAt: new Date(),
        reviewedBy: 'admin',
        rejectionReasons: reasons.reasons,
        rejectionMessage: customMessage
      });

      // Close dialog and clear reasons
      const newMap = new Map(rejectionReasons);
      newMap.set(claimId, { reasons: [], showDialog: false });
      setRejectionReasons(newMap);

      // Refresh the claims list
      fetchClaims();
      
      alert('Claim rejected successfully!');
    } catch (err) {
      alert('Failed to reject claim: ' + err.message);
    }
  };

  const cancelRejectClaim = (claimId) => {
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { reasons: [], showDialog: false });
    setRejectionReasons(newMap);
  };

  const toggleRejectionReason = (claimId, reason) => {
    const current = rejectionReasons.get(claimId) || { reasons: [], showDialog: true };
    const updatedReasons = current.reasons.includes(reason)
      ? current.reasons.filter(r => r !== reason)
      : [...current.reasons, reason];
    
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...current, reasons: updatedReasons });
    setRejectionReasons(newMap);
  };

  const setRejectionMessage = (claimId, customMessage) => {
    const current = rejectionReasons.get(claimId) || { reasons: [], showDialog: true };
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...current, customMessage });
    setRejectionReasons(newMap);
  };

  const handleChangeStatus = async (claimId, newStatus, dancerName, userId) => {
    try {
      const claimRef = doc(db, 'profileClaims', claimId);
      await updateDoc(claimRef, {
        status: newStatus,
        reviewedAt: new Date(),
        reviewedBy: 'admin'
      });

      if (newStatus === 'approved') {
        // Check if dancer document exists, create if it doesn't
        const dancerRef = doc(db, 'dancers', dancerName);
        const dancerDoc = await getDoc(dancerRef);
        
        const claimData = {
          claimedBy: {
            userId: userId,
            email: claims.find(c => c.id === claimId)?.userEmail || 'unknown',
            approvedAt: new Date()
          },
          claimedAt: new Date()
        };

        if (dancerDoc.exists()) {
          console.log(`Updating existing dancer document for: ${dancerName}`);
          await updateDoc(dancerRef, claimData);
        } else {
          console.log(`Creating new dancer document for: ${dancerName}`);
          await setDoc(dancerRef, {
            name: dancerName,
            ...claimData,
            createdAt: new Date(),
            lastUpdated: new Date()
          });
        }

        try {
          const profileRef = doc(db, 'dancerProfiles', dancerName);
          const claim = claims.find(c => c.id === claimId);
          const profileData = {
            profileClaimed: true,
            claimedBy: {
              userId: userId,
              email: claim?.userEmail || 'unknown',
              approvedAt: new Date()
            },
            claimedAt: new Date()
          };
          
          // Add Instagram data if available in the claim
          if (claim?.instagramUsername) {
            profileData.instagramUsername = claim.instagramUsername.replace('@', '');
          }
          
          // Add bio data if available in the claim
          if (claim?.bio) {
            profileData.bio = claim.bio;
          }
          
          await updateDoc(profileRef, profileData);
          
          // Also sync to dancers collection socialMedia structure
          if (claim?.instagramUsername) {
            const socialMediaData = {
              socialMedia: {
                instagram: claim.instagramUsername,
                facebook: '',
                tiktok: ''
              },
              bio: claim?.bio || '',
              lastUpdated: new Date()
            };

            if (dancerDoc.exists()) {
              await updateDoc(dancerRef, socialMediaData);
            } else {
              // Document was just created, merge the social media data
              await updateDoc(dancerRef, socialMediaData);
            }
          }
          
        } catch (profileError) {
          console.log('Note: dancerProfiles document may not exist yet:', profileError.message);
        }
      } else if (newStatus === 'rejected') {
        // Remove claim data from dancer profile when rejecting
        try {
          const dancerRef = doc(db, 'dancers', dancerName);
          const dancerDoc = await getDoc(dancerRef);
          
          if (dancerDoc.exists()) {
            await updateDoc(dancerRef, {
              claimedBy: null,
              claimedAt: null
            });
          }

          const profileRef = doc(db, 'dancerProfiles', dancerName);
          const profileDoc = await getDoc(profileRef);
          
          if (profileDoc.exists()) {
            await updateDoc(profileRef, {
              profileClaimed: false,
              claimedBy: null,
              claimedAt: null
            });
          }
        } catch (error) {
          console.log('Note: Error removing claim data from profile:', error.message);
        }
      }

      // Refresh the claims list
      fetchClaims();
      
      alert(`Claim ${newStatus} successfully!`);
    } catch (err) {
      alert(`Failed to change claim status: ${err.message}`);
    }
  };

  const handleDeleteClaim = async (claimId) => {
    if (!window.confirm('Are you sure you want to delete this claim? This action cannot be undone.')) {
      return;
    }

    try {
      const claimRef = doc(db, 'profileClaims', claimId);
      await deleteDoc(claimRef);

      // Refresh the claims list
      fetchClaims();
      
      alert('Claim deleted successfully!');
    } catch (err) {
      alert('Failed to delete claim: ' + err.message);
    }
  };

  const handleRestoreSpecificClaims = async () => {
    const dancersToRestore = ['Veronika Stancev', 'Konstantina Psoma'];
    setRestoreLoading(true);
    
    try {
      // First, let's see what claims actually exist
      console.log('=== INVESTIGATING ALL CLAIMS ===');
      const allClaimsRef = collection(db, 'profileClaims');
      const allClaimsSnapshot = await getDocs(allClaimsRef);
      
      console.log(`Total claims in database: ${allClaimsSnapshot.size}`);
      allClaimsSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log(`Claim: ${data.dancerName} | Status: ${data.status} | Email: ${data.userEmail}`);
      });
      
      // Also check all dancer profiles
      console.log('=== INVESTIGATING ALL DANCER PROFILES ===');
      const allDancersRef = collection(db, 'dancers');
      const allDancersSnapshot = await getDocs(allDancersRef);
      
      console.log(`Total dancer profiles: ${allDancersSnapshot.size}`);
      let foundDancers = [];
      allDancersSnapshot.forEach((doc) => {
        const dancerName = doc.id;
        const data = doc.data();
        if (dancerName.toLowerCase().includes('veronika') || 
            dancerName.toLowerCase().includes('stancev') ||
            dancerName.toLowerCase().includes('konstantina') ||
            dancerName.toLowerCase().includes('psoma')) {
          foundDancers.push({ name: dancerName, claimed: !!data.claimedBy });
          console.log(`Found related dancer: ${dancerName} | Claimed: ${!!data.claimedBy}`);
        }
      });
      
      // Now try to restore based on what we found
      for (const dancerName of dancersToRestore) {
        console.log(`\n=== PROCESSING: ${dancerName} ===`);
        
        // Special handling for Konstantina Psoma (no claim record exists)
        if (dancerName === 'Konstantina Psoma') {
          console.log('Special case: Konstantina Psoma (no claim record, but profile marked as claimed)');
          console.log('Found her real user ID: SelitOBvr8RPgDdla9VUoUl3d3E3 (konpsm24@gmail.com)');
          
          // Check if dancer profile exists
          const dancerRef = doc(db, 'dancers', dancerName);
          const dancerDoc = await getDoc(dancerRef);
          
          // Use her real user data instead of placeholder
          const realClaimedBy = {
            userId: 'SelitOBvr8RPgDdla9VUoUl3d3E3',
            email: 'konpsm24@gmail.com',
            approvedAt: new Date()
          };
          
          if (!dancerDoc.exists()) {
            console.log(`⚠️ Creating dancer profile for ${dancerName}...`);
            await setDoc(dancerRef, {
              name: dancerName,
              competitions: [], 
              totalPoints: 0,
              claimedBy: realClaimedBy,
              claimedAt: new Date(),
              createdBy: 'admin-restore-konstantina',
              createdAt: new Date(),
              isVerified: true
            });
            console.log(`✅ Created dancer profile for ${dancerName}`);
          } else {
            await updateDoc(dancerRef, {
              claimedBy: realClaimedBy,
              claimedAt: new Date(),
              isVerified: true
            });
            console.log(`✅ Updated dancer profile for ${dancerName}`);
          }
          
          // Update dancerProfiles collection to ensure claimedBy exists
          try {
            const profileRef = doc(db, 'dancerProfiles', dancerName);
            await updateDoc(profileRef, {
              profileClaimed: true,
              claimedBy: realClaimedBy,
              claimedAt: new Date()
            });
            console.log(`✅ Updated/Fixed dancerProfiles document for ${dancerName} with real user data`);
          } catch (profileError) {
            console.log(`Note: Error with dancerProfiles for ${dancerName}:`, profileError.message);
          }
          
          console.log(`✅ Successfully restored verified status for ${dancerName} (manual fix)`);
          continue;
        }
        
        // Regular handling for other dancers (like Veronika with existing claims)
        // Check if there are any claims for this dancer (exact match first)
        const claimsRef = collection(db, 'profileClaims');
        const q = query(claimsRef, where('dancerName', '==', dancerName));
        const claimsSnapshot = await getDocs(q);
        
        if (claimsSnapshot.empty) {
          console.log(`No exact claims found for ${dancerName}`);
          
          // Try to find similar names in claims
          const similarClaims = [];
          allClaimsSnapshot.forEach((doc) => {
            const data = doc.data();
            const claimDancerName = data.dancerName || '';
            if (claimDancerName.toLowerCase().includes(dancerName.split(' ')[0].toLowerCase()) ||
                claimDancerName.toLowerCase().includes(dancerName.split(' ')[1].toLowerCase())) {
              similarClaims.push({ id: doc.id, ...data });
            }
          });
          
          if (similarClaims.length > 0) {
            console.log(`Found ${similarClaims.length} similar claims:`, similarClaims.map(c => c.dancerName));
          } else {
            console.log(`No similar claims found for ${dancerName}`);
          }
          continue;
        }
        
        // Get the most recent claim
        const claimsData = [];
        claimsSnapshot.forEach((doc) => {
          claimsData.push({ id: doc.id, ...doc.data() });
        });
        
        claimsData.sort((a, b) => {
          const dateA = a.submittedAt?.toDate() || new Date(0);
          const dateB = b.submittedAt?.toDate() || new Date(0);
          return dateB - dateA;
        });
        
        const latestClaim = claimsData[0];
        console.log(`Found claim for ${dancerName}:`, {
          id: latestClaim.id,
          status: latestClaim.status,
          email: latestClaim.userEmail,
          userId: latestClaim.userId
        });
        
        // Check if dancer profile exists
        const dancerRef = doc(db, 'dancers', dancerName);
        const dancerDoc = await getDoc(dancerRef);
        
        if (!dancerDoc.exists()) {
          console.log(`⚠️ Dancer profile not found for ${dancerName}. Creating it...`);
          
          // Create a basic dancer profile
          await setDoc(dancerRef, {
            name: dancerName,
            competitions: [], // Empty initially
            totalPoints: 0,
            claimedBy: {
              userId: latestClaim.userId || 'restored-user',
              email: latestClaim.userEmail || 'unknown@restored.com',
              approvedAt: new Date()
            },
            claimedAt: new Date(),
            createdBy: 'admin-restore',
            createdAt: new Date()
          });
          console.log(`✅ Created dancer profile for ${dancerName}`);
        } else {
          // Update existing profile
          const dancerData = dancerDoc.data();
          console.log(`Dancer profile exists. Current claim status:`, !!dancerData.claimedBy);
          
          await updateDoc(dancerRef, {
            claimedBy: {
              userId: latestClaim.userId || 'restored-user',
              email: latestClaim.userEmail || 'unknown@restored.com',
              approvedAt: new Date()
            },
            claimedAt: new Date()
          });
          console.log(`✅ Updated dancer profile for ${dancerName}`);
        }
        
        // Update the claim status to approved
        const claimRef = doc(db, 'profileClaims', latestClaim.id);
        await updateDoc(claimRef, {
          status: 'approved',
          reviewedAt: new Date(),
          reviewedBy: 'admin-restore'
        });
        console.log(`✅ Updated claim status to approved for ${dancerName}`);
        
        // Update dancerProfiles collection
        try {
          const profileRef = doc(db, 'dancerProfiles', dancerName);
          const profileData = {
            profileClaimed: true,
            claimedBy: {
              userId: latestClaim.userId || 'restored-user',
              email: latestClaim.userEmail || 'unknown@restored.com',
              approvedAt: new Date()
            },
            claimedAt: new Date(),
            bio: latestClaim.bio || '',
            instagramUsername: latestClaim.instagramUsername?.replace('@', '') || ''
          };
          
          await setDoc(profileRef, profileData, { merge: true });
          console.log(`✅ Updated/Created dancerProfiles document for ${dancerName}`);
        } catch (profileError) {
          console.log(`Note: Error with dancerProfiles for ${dancerName}:`, profileError.message);
        }
        
        // Also update dancers collection with socialMedia structure for consistency
        try {
          const dancerRef = doc(db, 'dancers', dancerName);
          const socialMediaData = {
            socialMedia: {
              instagram: latestClaim.instagramUsername || '',
              facebook: '',
              tiktok: ''
            },
            bio: latestClaim.bio || '',
            lastUpdated: new Date()
          };
          
          await updateDoc(dancerRef, socialMediaData);
          console.log(`✅ Synced social media data to dancers collection for ${dancerName}`);
        } catch (socialError) {
          console.log(`Note: Error syncing social media for ${dancerName}:`, socialError.message);
        }
        
        console.log(`✅ Successfully restored verified status for ${dancerName}`);
      }
      
      // Refresh the claims list
      fetchClaims();
      
      alert('Restoration process completed! Check console for detailed logs.');
    } catch (err) {
      console.error('Restoration error:', err);
      alert('Failed to restore claims: ' + err.message);
    } finally {
      setRestoreLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Pending', color: '#f39c12', bgColor: '#fef9e7' },
      approved: { text: 'Approved', color: '#27ae60', bgColor: '#eafaf1' },
      rejected: { text: 'Rejected', color: '#e74c3c', bgColor: '#fdedec' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span 
        style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold',
          color: badge.color,
          backgroundColor: badge.bgColor,
          border: `1px solid ${badge.color}`
        }}
      >
        {badge.text}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const toggleDocumentView = (claimId) => {
    const newExpanded = new Set(expandedDocuments);
    if (newExpanded.has(claimId)) {
      newExpanded.delete(claimId);
    } else {
      newExpanded.add(claimId);
    }
    setExpandedDocuments(newExpanded);
  };

  const renderDocumentViewer = (claim) => {
    // Check if we have documents from Firebase Storage for this dancer
    const dancerDocs = dancerDocuments.get(claim.dancerName) || [];
    
    // Start with Firebase Storage documents
    let documentsToShow = [...dancerDocs];
    
    // Always add claim URLs if they exist and aren't already in the list
    if (claim.documentURL) {
      const alreadyExists = documentsToShow.some(doc => doc.url === claim.documentURL);
      if (!alreadyExists) {
        documentsToShow.push({ name: 'Front Side', url: claim.documentURL, source: 'claim-front' });
      }
    }
    if (claim.documentURLBack) {
      const alreadyExists = documentsToShow.some(doc => doc.url === claim.documentURLBack);
      if (!alreadyExists) {
        documentsToShow.push({ name: 'Back Side', url: claim.documentURLBack, source: 'claim-back' });
      }
    }
    
    if (documentsToShow.length === 0) {
      return (
        <div className="claim-field" style={{ color: '#dc3545' }}>
          <strong>Document:</strong> <em>No documents found in Firebase Storage folders</em>
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
            Searched: /verification-docs/{claim.dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}/, /verification-docs/ (by name), and /dancers/{claim.dancerName}
          </div>
        </div>
      );
    }
    
    const isExpanded = expandedDocuments.has(claim.id);
    
    return (
      <div className="document-viewer" style={{ marginTop: '1rem' }}>
        <div className="claim-field">
          <strong>Documents Found:</strong> {documentsToShow.length} file(s) in Firebase Storage
          {documentsToShow.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.2rem' }}>
              Sources: {[...new Set(documentsToShow.map(doc => doc.source || 'unknown'))].join(', ')}
            </div>
          )}
        </div>
        
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          alignItems: 'center',
          marginBottom: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => toggleDocumentView(claim.id)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: isExpanded ? '#dc3545' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {isExpanded ? '🔽 Hide Documents' : `📄 View ${documentsToShow.length} Document(s) Inline`}
          </button>
          
          {documentsToShow.map((doc, index) => (
            <a 
              key={index}
              href={doc.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            >
              🔗 Open {doc.name}
            </a>
          ))}
        </div>
        
        {isExpanded && (
          <div style={{
            border: '2px solid #dee2e6',
            borderRadius: '8px',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            maxHeight: '700px',
            overflow: 'auto'
          }}>
            {documentsToShow.map((document, index) => {
              const fileExtension = document.name.split('.').pop()?.toLowerCase();
              const isPDF = fileExtension === 'pdf';
              const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
              
              return (
                <div key={index} style={{ marginBottom: index < documentsToShow.length - 1 ? '2rem' : '0' }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: '#333',
                    borderBottom: '1px solid #dee2e6',
                    paddingBottom: '0.5rem'
                  }}>
                    📄 {document.name} ({fileExtension?.toUpperCase()})
                    {document.source && (
                      <span style={{ 
                        fontSize: '0.7rem', 
                        color: '#666', 
                        fontWeight: 'normal',
                        marginLeft: '0.5rem',
                        padding: '2px 6px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '3px'
                      }}>
                        {document.source}
                      </span>
                    )}
                  </h4>
                  
                  {isPDF ? (
                    <div>
                      <iframe
                        src={document.url}
                        style={{
                          width: '100%',
                          height: '500px',
                          border: 'none',
                          borderRadius: '4px'
                        }}
                        title={`${document.name} for ${claim.dancerName}`}
                      />
                      <p style={{ margin: '1rem 0 0 0', fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
                        If the PDF doesn't load, click "Open {document.name}" above
                      </p>
                    </div>
                  ) : isImage ? (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={document.url}
                        alt={`${document.name} for ${claim.dancerName}`}
                        crossOrigin="anonymous"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '500px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        onError={(e) => {
                          console.error(`Failed to load image: ${document.name}`, document.url);
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                        onLoad={() => {
                          console.log(`✅ Successfully loaded image: ${document.name}`);
                        }}
                      />
                      <div style={{ display: 'none', color: '#dc3545', marginTop: '1rem' }}>
                        ❌ Failed to load image. Please use "Open {document.name}" to view the document.
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: 'white', borderRadius: '4px' }}>
                      <p style={{ color: '#666', marginBottom: '1rem' }}>
                        📄 Document type: {fileExtension?.toUpperCase() || 'Unknown'}
                      </p>
                      <p style={{ color: '#666', marginBottom: '1rem' }}>
                        Preview not available for this file type. Use "Open {document.name}" to view the document.
                      </p>
                      <div style={{ fontSize: '0.8rem', color: '#999', wordBreak: 'break-all' }}>
                        File: {document.name} | Source: {document.source || 'unknown'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="admin-claim-review loading">Loading claims...</div>;
  }

  if (error) {
    return <div className="admin-claim-review error">{error}</div>;
  }

  return (
    <div className="admin-claim-review">
      <h1>Profile Claim Review</h1>
      
      {/* Status Filter */}
      <div className="filter-section" style={{ 
        marginBottom: '2rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Filter by Status</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { value: 'pending', label: 'Pending', count: claims.filter(c => c.status === 'pending').length },
            { value: 'approved', label: 'Approved', count: claims.filter(c => c.status === 'approved').length },
            { value: 'rejected', label: 'Rejected', count: claims.filter(c => c.status === 'rejected').length },
            { value: 'all', label: 'All', count: claims.length }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              style={{
                padding: '0.5rem 1rem',
                border: statusFilter === filter.value ? '2px solid #007bff' : '1px solid #ccc',
                borderRadius: '6px',
                backgroundColor: statusFilter === filter.value ? '#007bff' : 'white',
                color: statusFilter === filter.value ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: statusFilter === filter.value ? 'bold' : 'normal'
              }}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>
      
      {/* Restore Claims Section */}
      <div className="restore-section" style={{ 
        marginBottom: '2rem', 
        padding: '1rem', 
        backgroundColor: '#fff3cd', 
        borderRadius: '8px',
        border: '1px solid #ffeaa7'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#856404' }}>Restore Verified Status</h3>
        <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#856404' }}>
          Restore verified status for Veronika Stancev and Konstantina Psoma if they were previously approved.
        </p>
        <button
          onClick={handleRestoreSpecificClaims}
          disabled={restoreLoading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#f39c12',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: restoreLoading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            opacity: restoreLoading ? 0.6 : 1
          }}
        >
          {restoreLoading ? '🔄 Restoring...' : '🔧 Restore Veronika & Konstantina Claims'}
        </button>
      </div>
      
      {visibleClaims.length === 0 ? (
        <div className="no-claims">
          No {statusFilter === 'all' ? '' : statusFilter} claims to show.
        </div>
      ) : (
        <div className="claims-list">
          {visibleClaims.map((claim) => {
            const isExpanded = expandedClaims.has(claim.id);
            const isLoadingDetails = loadingDetails.has(claim.id);
            
            return (
            <div key={claim.id} className="claim-item" style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: isExpanded ? '1.5rem' : '1rem',
              marginBottom: '1rem',
              backgroundColor: 'white',
              cursor: isExpanded ? 'default' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => !isExpanded && toggleClaimExpansion(claim.id, claim)}
            >
              {/* Compact Header - Always visible */}
              <div className="claim-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '1', minWidth: '200px' }}>
                  {/* Profile Picture Thumbnail */}
                  <Link 
                    to={`/dancer/${encodeURIComponent(claim.dancerName)}`} 
                    style={{ textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {dancerProfilePics.get(claim.dancerName) ? (
                      <img 
                        src={dancerProfilePics.get(claim.dancerName)} 
                        alt={claim.dancerName}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #ddd'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: '#e9ecef',
                      display: dancerProfilePics.get(claim.dancerName) ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      color: '#6c757d',
                      border: '2px solid #ddd'
                    }}>
                      👤
                    </div>
                  </Link>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <Link
                      to={`/dancer/${encodeURIComponent(claim.dancerName)}`}
                      style={{
                        textDecoration: 'none',
                        color: '#007bff',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {claim.dancerName}
                    </Link>
                    <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span>🎂 {claim.birthDate || 'No DOB'}</span>
                      <span>📋 {calculateAgeCategory(claim.birthDate) || 'Unknown'}</span>
                      {(() => {
                        const studios = dancerStudios.get(claim.dancerName) || [];
                        if (studios.length === 0) return null;
                        if (studios.length === 1) return <span>🏫 {studios[0]}</span>;
                        return (
                          <select
                            defaultValue={studios[0]}
                            style={{ fontSize: '0.8rem', padding: '0.1rem 0.3rem', borderRadius: '4px', border: '1px solid #ccc', color: '#444', backgroundColor: 'white', cursor: 'pointer' }}
                            onClick={e => e.stopPropagation()}
                          >
                            {studios.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {getStatusBadge(claim.status)}
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>
                    {formatDate(claim.submittedAt)}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleClaimExpansion(claim.id, claim); }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: isExpanded ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    {isLoadingDetails ? '⏳ Loading...' : isExpanded ? '▲ Collapse' : '▼ View Details'}
                  </button>
                </div>
              </div>
              
              {/* Expanded Details - Only shown when expanded */}
              {isExpanded && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>

              {/* Quick Actions for Pending Claims */}
              {claim.status === 'pending' && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ flex: 1, marginRight: '1rem' }}>
                      <strong style={{ color: '#856404' }}>⚡ Quick Decision</strong>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.85rem', color: '#856404', whiteSpace: 'nowrap' }}>Approve as name:</label>
                        <input
                          type="text"
                          value={adminNameOverrides[claim.id] !== undefined ? adminNameOverrides[claim.id] : claim.dancerName}
                          onChange={(e) => setAdminNameOverrides(prev => ({ ...prev, [claim.id]: e.target.value }))}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: (adminNameOverrides[claim.id] !== undefined && adminNameOverrides[claim.id] !== claim.dancerName) ? '2px solid #856404' : '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            minWidth: '160px',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {adminNameOverrides[claim.id] !== undefined && adminNameOverrides[claim.id] !== claim.dancerName && adminNameOverrides[claim.id].trim() && (
                          <span style={{ fontSize: '0.78rem', color: '#856404', fontWeight: 600 }}>
                            → will rename in all competition results
                          </span>
                        )}
                        {adminNameOverrides[claim.id] !== undefined && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setAdminNameOverrides(prev => { const n = {...prev}; delete n[claim.id]; return n; }); }}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                          >Reset</button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleApproveClaim(claim.id, claim.dancerName, claim.userId, adminDobOverrides[claim.id])}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ✅ APPROVE
                      </button>
                      <button
                        onClick={() => handleRejectClaim(claim.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ❌ REJECT
                      </button>
                    </div>
                  </div>
                  <small style={{ color: '#856404' }}>
                    Review the document below, then make your decision
                  </small>
                </div>
              )}

              {/* Rejection Reasons Dialog for Dancer Claims */}
              {rejectionReasons.get(claim.id)?.showDialog && (
                <div style={{
                  backgroundColor: '#fff',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{ color: '#dc3545', marginTop: 0 }}>Select Rejection Reason(s)</h4>
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>Choose a reason and/or write a custom message. Both are e-mailed to the claimant:</p>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={rejectionReasons.get(claim.id)?.reasons.includes('Incorrect Birth Date') || false}
                        onChange={() => toggleRejectionReason(claim.id, 'Incorrect Birth Date')}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <strong>Incorrect Birth Date</strong> - Birth Date does not correspond to the official document
                    </label>
                    
                    <label style={{ display: 'block', marginBottom: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={rejectionReasons.get(claim.id)?.reasons.includes('Incorrect Verification Document') || false}
                        onChange={() => toggleRejectionReason(claim.id, 'Incorrect Verification Document')}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <strong>Incorrect Verification Document</strong> - Document provided is not valid or does not match
                    </label>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 'bold' }}>
                      Custom message to the claimant (optional)
                    </label>
                    <textarea
                      value={rejectionReasons.get(claim.id)?.customMessage || ''}
                      onChange={(e) => setRejectionMessage(claim.id, e.target.value)}
                      maxLength={1000}
                      rows={4}
                      placeholder="e.g. The birth date on the photo of your ID is not readable. Please upload a clearer photo."
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => cancelRejectClaim(claim.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmRejectClaim(claim.id, false)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Confirm Rejection
                    </button>
                  </div>
                </div>
              )}
              
              <div className="claim-details">
                <div className="claim-field">
                  <strong>User Email:</strong> {claim.userEmail}
                </div>
                <div className="claim-field">
                  <strong>User ID:</strong> {claim.userId || 'Not provided'}
                </div>
                <div className="claim-field">
                  <strong>Birth Date:</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      value={
                        adminDobOverrides[claim.id] !== undefined
                          ? adminDobOverrides[claim.id]
                          : (claim.birthDate
                              ? (claim.birthDate.includes('/') // convert dd/mm/yyyy to yyyy-mm-dd
                                  ? (() => { const [d,m,y] = claim.birthDate.split('/'); return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`; })()
                                  : claim.birthDate)
                              : '')
                      }
                      onChange={(e) => setAdminDobOverrides(prev => ({ ...prev, [claim.id]: e.target.value }))}
                      style={{
                        padding: '0.3rem 0.5rem',
                        border: adminDobOverrides[claim.id] !== undefined ? '2px solid #667eea' : '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        background: '#fff',
                        color: '#1a202c',
                        colorScheme: 'light',
                      }}
                    />
                    {adminDobOverrides[claim.id] !== undefined && adminDobOverrides[claim.id] !== (claim.birthDate || '') && (
                      <span style={{ fontSize: '0.78rem', color: '#667eea', fontWeight: 600 }}>✏️ Overridden</span>
                    )}
                    {adminDobOverrides[claim.id] !== undefined && (
                      <button
                        onClick={() => setAdminDobOverrides(prev => { const n = {...prev}; delete n[claim.id]; return n; })}
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                      >Reset</button>
                    )}
                  </div>
                </div>
                <div className="claim-field">
                  <strong>Age Category:</strong>{' '}
                  {(() => {
                    const dob = adminDobOverrides[claim.id] !== undefined ? adminDobOverrides[claim.id] : claim.birthDate;
                    return calculateAgeCategory(dob) || 'Unknown';
                  })()}
                  {claim.ageCategory && (() => {
                    const dob = adminDobOverrides[claim.id] !== undefined ? adminDobOverrides[claim.id] : claim.birthDate;
                    return calculateAgeCategory(dob) !== claim.ageCategory;
                  })() && (
                    <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontSize: '0.85em' }}>
                      (Stored: {claim.ageCategory} - Corrected!)
                    </span>
                  )}
                </div>
                {claim.documentType && (
                  <div className="claim-field">
                    <strong>Document Type:</strong> {claim.documentType}
                  </div>
                )}
                
                {renderDocumentViewer(claim)}
                
                {/* Debug Information */}
                <details style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#666' }}>🔍 Debug Info</summary>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify({
                      claimId: claim.id,
                      dancerName: claim.dancerName,
                      sanitizedName: claim.dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_'),
                      status: claim.status,
                      documentURL: claim.documentURL,
                      documentPath: claim.documentPath, // New field
                      documentFileName: claim.documentFileName, // New field
                      documentType: claim.documentType,
                      firebaseStorageDocs: dancerDocuments.get(claim.dancerName)?.map(doc => ({ 
                        name: doc.name, 
                        path: doc.fullPath,
                        source: doc.source 
                      })) || 'None found',
                      expectedFolder: `verification-docs/${claim.dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}/`,
                      submittedAt: claim.submittedAt?.toDate?.()?.toISOString() || claim.submittedAt,
                      reviewedAt: claim.reviewedAt?.toDate?.()?.toISOString() || claim.reviewedAt
                    }, null, 2)}
                  </pre>
                </details>
              </div>
              
              <div className="claim-actions" style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginTop: '1rem',
                flexWrap: 'wrap'
              }}>
                {claim.status === 'pending' && (
                  <>
                    <button
                      className="approve-btn"
                      onClick={() => handleApproveClaim(claim.id, claim.dancerName, claim.userId, adminDobOverrides[claim.id])}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleRejectClaim(claim.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
                
                {claim.status === 'approved' && (
                  <button
                    onClick={() => handleChangeStatus(claim.id, 'rejected', claim.dancerName, claim.userId)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Change to Rejected
                  </button>
                )}
                
                {claim.status === 'rejected' && (
                  <button
                    onClick={() => handleChangeStatus(claim.id, 'approved', claim.dancerName, claim.userId)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Change to Approved
                  </button>
                )}

                <button
                  className="delete-btn"
                  onClick={() => handleDeleteClaim(claim.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
              
              </div>
              )}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminClaimReview;
